import { Component } from '@theme/component';
import { CollectionUpdateEvent, StandardEvents } from '@shopify/events';

const VIEW_PARAM = 'gp_coverage';
const VIEW_ALL = 'all';
const VIEW_BARE_BACK = 'bare-back';
const VIEW_FULL_COVERAGE = 'full-coverage';
const VALID_VIEWS = new Set([VIEW_ALL, VIEW_BARE_BACK, VIEW_FULL_COVERAGE]);

/**
 * Adds Gruff Pup's Singlet coverage views without replacing Fabric's native
 * collection filtering, sorting, pagination, or Section Rendering behavior.
 */
class SingletCollectionViewComponent extends Component {
  /** @type {string} */
  #view = VIEW_ALL;

  /** @type {MutationObserver | null} */
  #gridObserver = null;

  /** @type {WeakMap<Element, string>} */
  #nativeText = new WeakMap();

  connectedCallback() {
    super.connectedCallback();

    this.#view = this.#readViewFromURL();
    this.#captureNativeText();
    this.#observeGrid();
    this.#applyView();

    window.addEventListener('popstate', this.#handlePopState);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleCollectionUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#gridObserver?.disconnect();
    this.#gridObserver = null;
    window.removeEventListener('popstate', this.#handlePopState);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleCollectionUpdate);
  }

  /**
   * Select a Singlet coverage view from one of the rendered buttons.
   * @param {Event} event
   */
  selectView(event) {
    if (!(event.target instanceof HTMLButtonElement)) return;

    const view = event.target.dataset.view;
    if (!view || !VALID_VIEWS.has(view) || view === this.#view) return;

    this.#view = view;
    this.#syncURL('push');
    this.#applyView();
  }

  #readViewFromURL() {
    const view = new URL(window.location.href).searchParams.get(VIEW_PARAM) ?? VIEW_ALL;
    return VALID_VIEWS.has(view) ? view : VIEW_ALL;
  }

  /**
   * @param {'push' | 'replace'} mode
   */
  #syncURL(mode) {
    const url = new URL(window.location.href);

    if (this.#view === VIEW_ALL) {
      url.searchParams.delete(VIEW_PARAM);
    } else {
      url.searchParams.set(VIEW_PARAM, this.#view);
    }

    const method = mode === 'push' ? 'pushState' : 'replaceState';
    history[method](history.state, '', url.toString());
  }

  #handlePopState = () => {
    this.#view = this.#readViewFromURL();
    this.#applyView();
  };

  /**
   * Fabric rebuilds native facet URLs from its form values, so the Gruff view
   * parameter is restored after each native filter/sort change. The parameter
   * does not affect Shopify's server query; the freshly rendered cards retain
   * their server-derived coverage classification and are intersected here.
   * @param {CollectionUpdateEvent} event
   */
  #handleCollectionUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    const mySection = this.closest('[id^="shopify-section-"]');
    if (eventSection && mySection && eventSection !== mySection) return;

    this.#syncURL('replace');
    event.promise.then(this.#refreshAfterSectionRender, this.#refreshAfterSectionRender);
  };

  #refreshAfterSectionRender = () => {
    if (!this.isConnected) return;

    this.#captureNativeText(true);
    this.#observeGrid();
    this.#applyView();
  };

  #getResultsList() {
    return this.closest('results-list');
  }

  #getGrid() {
    return this.#getResultsList()?.querySelector('[data-testid="product-grid"]') ?? null;
  }

  #observeGrid() {
    this.#gridObserver?.disconnect();
    this.#gridObserver = null;

    const grid = this.#getGrid();
    if (!grid) return;

    this.#gridObserver = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.type === 'childList' && mutation.addedNodes.length > 0)) {
        this.#applyView();
      }
    });
    this.#gridObserver.observe(grid, { childList: true });
  }

  #applyView() {
    const buttons = Array.isArray(this.refs.viewButtons) ? this.refs.viewButtons : [];
    for (const button of buttons) {
      if (!(button instanceof HTMLButtonElement)) continue;
      button.setAttribute('aria-pressed', String(button.dataset.view === this.#view));
    }

    const grid = this.#getGrid();
    if (!grid) {
      this.#setEmptyState(false);
      this.#updateNativeCounts(this.#view === VIEW_ALL ? null : 0);
      this.#syncPaginationLinks();
      return;
    }

    const cards = Array.from(
      grid.querySelectorAll(':scope > [data-gp-singlet-bare-back][data-gp-singlet-full-coverage]')
    );
    let visibleCount = 0;

    for (const card of cards) {
      const isVisible =
        this.#view === VIEW_ALL ||
        (this.#view === VIEW_BARE_BACK && card.getAttribute('data-gp-singlet-bare-back') === 'true') ||
        (this.#view === VIEW_FULL_COVERAGE && card.getAttribute('data-gp-singlet-full-coverage') === 'true');

      card.toggleAttribute('hidden', !isVisible);
      if (isVisible) visibleCount += 1;
    }

    this.#setEmptyState(this.#view !== VIEW_ALL && cards.length > 0 && visibleCount === 0);
    this.#updateNativeCounts(this.#view === VIEW_ALL ? null : visibleCount);
    this.#syncPaginationLinks();
  }

  /**
   * Preserve Fabric's native count labels so selecting All restores them exactly.
   * @param {boolean} force
   */
  #captureNativeText(force = false) {
    const resultsList = this.#getResultsList();
    if (!resultsList) return;

    const elements = resultsList.querySelectorAll('.products-count-wrapper [role="status"], .facets__see-results');
    for (const element of elements) {
      if (force || !this.#nativeText.has(element)) {
        this.#nativeText.set(element, element.textContent ?? '');
      }
    }
  }

  /**
   * @param {number | null} count Null restores Fabric's server-rendered labels.
   */
  #updateNativeCounts(count) {
    const resultsList = this.#getResultsList();
    if (!resultsList) return;

    const countTemplate = this.dataset.countTemplate ?? '__COUNT__';
    const seeResultsTemplate = this.dataset.seeResultsTemplate ?? '__COUNT__';

    for (const element of resultsList.querySelectorAll('.products-count-wrapper [role="status"]')) {
      if (count === null) {
        const nativeText = this.#nativeText.get(element);
        if (nativeText !== undefined) element.textContent = nativeText;
      } else {
        element.textContent = countTemplate.replaceAll('__COUNT__', String(count));
      }
    }

    for (const element of resultsList.querySelectorAll('.facets__see-results')) {
      if (count === null) {
        const nativeText = this.#nativeText.get(element);
        if (nativeText !== undefined) element.textContent = nativeText;
      } else {
        element.textContent = seeResultsTemplate.replaceAll('__COUNT__', String(count));
      }
    }
  }

  /**
   * @param {boolean} show
   */
  #setEmptyState(show) {
    const { emptyState } = this.refs;
    if (emptyState instanceof HTMLElement) emptyState.hidden = !show;
  }

  #syncPaginationLinks() {
    const resultsList = this.#getResultsList();
    if (!resultsList) return;

    for (const link of resultsList.querySelectorAll('.pagination a[href]')) {
      if (!(link instanceof HTMLAnchorElement)) continue;

      const url = new URL(link.href, window.location.origin);
      if (this.#view === VIEW_ALL) {
        url.searchParams.delete(VIEW_PARAM);
      } else {
        url.searchParams.set(VIEW_PARAM, this.#view);
      }

      link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
    }
  }
}

if (!customElements.get('singlet-collection-view-component')) {
  customElements.define('singlet-collection-view-component', SingletCollectionViewComponent);
}
